<template>
  <Table
    :class="{
      'table-dark': props.dark,
      'table-bordered': props.bordered,
      'table-hover': props.hover,
      'table-sm': props.small
    }"
  >
    <template v-if="props.busy">
      <TableLoading
        :columns="columns"
        :spinner="props.spinner"
        :busy-rows="props.busyRows"
      />
    </template>
    <template v-else>
      <TableHeader>
        <TableRow
          v-for="headerGroup in table.getHeaderGroups()"
          :key="headerGroup.id"
        >
          <TableHead
            v-for="header in headerGroup.headers"
            :key="header.id"
            :class="[
              header.column.getCanSort() ? 'cursor-pointer user-select-none' : '',
              header.getContext().header.column.columnDef?.class || ''
            ]"
            :data-testid="`${header.column.id}-col-head`"
            :aria-sort="header.column.getCanSort() ? { asc: 'ascending', desc: 'descending'}[header.column.getIsSorted()] || 'none' : null"
            class="position-relative b-table-sort-icon-left"
            @handle-click="header.column.getToggleSortingHandler()?.($event)"
          >
            <slot
              :name="`head(${header.column.id})`"
              :label="header.column.columnDef.header"
            >
              {{ header.column.columnDef.header }}
            </slot>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="table.getTopRows().length">
          <!-- <TableRows :rows="table.getTopRows()" /> -->
          <template
            v-for="(row, index) in table.getTopRows()"
            :key="row.original[keyName]"
          >
            <TableRow
              :data-row-id="row.original[keyName]"
              :data-state="row.getIsSelected() ? 'selected' : undefined"
              @row-hovered="emits('row-hovered', row.original)"
              @row-unhovered="emits('row-unhovered', row.original)"
            >
              <TableCell
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :class="cell.getContext().column.columnDef?.class"
              >
                <slot
                  :name="`cell(${cell.column.id})`"
                  :value="row.getValue(cell.column.id)"
                  :item="row.original"
                  :rowindex="index"
                  :can-expand="row.getCanExpand()"
                  :toggle-details="() => table.setExpanded({ [row.id]: !row.getIsExpanded() })"
                  :details-showing="row.getIsExpanded()"
                >
                  <div :data-testid="`${cell.column.id}-cell`">
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </div>
                </slot>
              </TableCell>
            </TableRow>
            <TableRow v-if="row.getIsExpanded()">
              <td :colspan="columns.length">
                <slot
                  name="row-details"
                  :item="row.original"
                />
              </td>
            </TableRow>
          </template>
        </template>
        <template v-if="table.getCenterRows().length">
          <!-- <TableRows :rows="table.getCenterRows()" /> -->
          <template
            v-for="(row, index) in table.getCenterRows()"
            :key="row.original[keyName]"
          >
            <TableRow
              :data-state="row.getIsSelected() ? 'selected' : undefined"
              :data-row-id="row.original[keyName]"
              @row-hovered="emits('row-hovered', row.original)"
              @row-unhovered="emits('row-unhovered', row.original)"
            >
              <TableCell
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :class="cell.getContext().column.columnDef?.class"
              >
                <slot
                  :key="cell.column.id"
                  :name="`cell(${cell.column.id})`"
                  :value="row.getValue(cell.column.id)"
                  :item="row.original"
                  :index="index + table.getTopRows().length"
                  :can-expand="row.getCanExpand()"
                  :toggle-details="() => table.setExpanded({ [row.id]: !row.getIsExpanded() })"
                  :details-showing="row.getIsExpanded()"
                >
                  <div :data-testid="`${cell.column.id}-cell`">
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </div>
                </slot>
              </TableCell>
            </TableRow>
            <TableRow v-if="row.getIsExpanded()">
              <td :colspan="columns.length">
                <slot
                  name="row-details"
                  :item="row.original"
                />
              </td>
            </TableRow>
          </template>
        </template>
        <template v-if="table.getBottomRows().length">
          <!-- <TableRows :rows="table.getBottomRows()" /> -->
          <template
            v-for="(row, index) in table.getBottomRows()"
            :key="row.original[keyName]"
          >
            <TableRow
              :data-state="row.getIsSelected() ? 'selected' : undefined"
              :data-row-id="row.original[keyName]"
              @row-hovered="emits('row-hovered', row.original)"
              @row-unhovered="emits('row-unhovered', row.original)"
            >
              <TableCell
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :class="cell.getContext().column.columnDef?.class"
              >
                <slot
                  :name="`cell(${cell.column.id})`"
                  :value="row.getValue(cell.column.id)"
                  :item="row.original"
                  :index="index + table.getTopRows().length + table.getCenterRows().length"
                  :can-expand="row.getCanExpand()"
                  :toggle-details="() => table.setExpanded({ [row.id]: !row.getIsExpanded() })"
                  :details-showing="row.getIsExpanded()"
                >
                  <div :data-testid="`${cell.column.id}-cell`">
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </div>
                </slot>
              </TableCell>
            </TableRow>
            <TableRow v-if="row.getIsExpanded()">
              <td :colspan="columns.length">
                <slot
                  name="row-details"
                  :item="row.original"
                />
              </td>
            </TableRow>
          </template>
        </template>

        <template v-if="!table.getRowModel().rows.length">
          <TableEmpty :colspan="columns.length">
            <slot name="empty">
              No data to show...
            </slot>
          </TableEmpty>
        </template>
      </TableBody>
    </template>
  </Table>
</template>

<script setup>

import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  useVueTable,
  FlexRender
} from '@tanstack/vue-table'
import Table from './Table.vue'
import TableBody from './TableBody.vue'
import TableCell from './TableCell.vue'
import TableHead from './TableHead.vue'
import TableHeader from './TableHeader.vue'
import TableRow from './TableRow.vue'
// import TableRows from './TableRows.vue'
import TableEmpty from './TableEmpty.vue'
import TableLoading from './TableLoading.vue'
import { ref, watch } from 'vue'

const props = defineProps({
  fields: {
    type: Array,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  keyName: {
    type: String,
    default: 'id'
  },
  perPage: {
    type: Number,
    default: undefined
  },
  currentPage: {
    type: Number,
    default: undefined
  },
  sortBy: {
    type: String,
    default: undefined
  },
  pinnedTop: {
    type: Function,
    default: undefined
  },
  pinnedBottom: {
    type: Function,
    default: undefined
  },
  busyRows: {
    type: Number,
    default: 5
  },
  sortDesc: Boolean,
  busy: Boolean,
  dark: Boolean,
  spinner: Boolean,
  bordered: Boolean,
  hover: Boolean,
  small: Boolean
})

const data = ref([])
const columns = ref()
const sorting = ref([])
const expanded = ref({})

const emits = defineEmits(['update:sortBy', 'update:sortDesc', 'row-hovered', 'row-unhovered', 'rows'])

const transformFields = (d) => {
  // transform from BTable format to vue-table format
  // change key property to accessorKey
  // change sortable to enableSorting
  // change label to header
  return d.map((field) => {
    return {
      accessorKey: field.key,
      enableSorting: field.sortable || false,
      header: field.label,
      ...field
    }
  })
}

watch(() => props.fields, (val) => {
  columns.value = transformFields(val)
}, {
  immediate: true,
  deep: true
})

// setting the default sort.
if (props.sortBy) {
  sorting.value = [
    {
      id: props.sortBy,
      desc: props.sortDesc
    }
  ]
}

const emitRows = (t) => {
  emits('rows', t.getPaginationRowModel().rows.map(r => r.original))
}

const table = useVueTable({
  get data() {
    return data.value
  },
  get columns() {
    return columns.value
  },
  enableSortingRemoval: false,
  enableRowPinning: true,
  keepPinnedRows: true,
  enableExpanding: true,
  manualExpanding: true,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  getSubRows: (row) => row.subRows,
  manualPagination: !props.perPage,
  state: {
    get sorting() {
      return sorting.value
    },
    get pagination() {
      if (!props.perPage) return undefined
      return {
        pageIndex: props.currentPage - 1,
        pageSize: props.perPage
      }
    },
    get expanded() {
      return expanded.value
    }
  },
  onExpandedChange: (updatedValue) => {
    if (!updatedValue || Object.keys(updatedValue).length === 0) {
      // resetting expanded value
      expanded.value = {}
      return
    }
    expanded.value = {
      ...expanded.value,
      ...updatedValue
    }
  },
  onSortingChange: (updaterOrValue) => {
    sorting.value
      = typeof updaterOrValue === 'function'
        ? updaterOrValue(sorting.value)
        : updaterOrValue

    emits('update:sortBy', sorting.value[0].id)
    emits('update:sortDesc', sorting.value[0].desc)
    emitRows(table)
  }
})

watch(() => props.items, (val) => {
  if (!val) return
  data.value = val.slice()
  emitRows(table)
}, {
  immediate: true,
  deep: true
})

watch(() => props.currentPage, () => {
  emitRows(table)
})

if (props.pinnedTop) {
  const top = table.getCenterRows().filter((row) => props.pinnedTop(row.original)).map(r => r.id)
  table.setRowPinning({ top })
}

if (props.pinnedBottom) {
  table.setRowPinning({
    bottom: table.getCenterRows().filter((row) => props.pinnedBottom(row.original)).map(r => r.id)
  })
}

</script>
